import type { Filter } from '@fabric-ui/components';

type Op = 'and' | 'or';

const flatten = (op: Op, f: Filter): Filter[] => (op in f && f[op]?.operands ? f[op].operands : [f]);

/**
 * Merges filters under the given operator, pulling root-level same-operator operands into a single flat list.
 * Only flattens the top level of each argument — nested groups deeper than one level are preserved.
 * Different-operator children are kept intact.
 *
 * @example
 * ```ts
 * merge('and', { and: { operands: [exprA, exprB] } }, exprC) // returns { and: { operands: [exprA, exprB, exprC] } }
 * ```
 */
export const merge = (op: Op, ...filters: Filter[]): Filter => ({
  [op]: { operands: filters.flatMap(f => flatten(op, f)) },
});

/**
 * Merges filters with AND, pulling root-level AND operands into a single flat list.
 * Only flattens the top level — nested groups are preserved.
 *
 * @example
 * ```ts
 * mergeAnd({ and: { operands: [exprA, exprB] } }, exprC) // returns { and: { operands: [exprA, exprB, exprC] } }
 * ```
 */
export const mergeAnd = (...filters: Filter[]) => merge('and', ...filters);

/**
 * Merges filters with OR, pulling root-level OR operands into a single flat list.
 * Only flattens the top level — nested groups are preserved.
 *
 * @example
 * ```ts
 * mergeOr({ or: { operands: [exprA, exprB] } }, exprC) // returns { or: { operands: [exprA, exprB, exprC] } }
 * ```
 */
export const mergeOr = (...filters: Filter[]) => merge('or', ...filters);
